import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GitBranch, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";

const DIFFICULTY_COLOR = {
  Beginner: "from-emerald-500/20 to-emerald-500/0 border-emerald-400/30 text-emerald-300",
  Intermediate: "from-indigo-500/20 to-indigo-500/0 border-indigo-400/30 text-indigo-300",
  Advanced: "from-purple-500/20 to-purple-500/0 border-purple-400/30 text-purple-300",
};

const ACCENT_TEXT = { indigo: "text-indigo-400", teal: "text-teal-400", fuchsia: "text-fuchsia-400", amber: "text-amber-400", emerald: "text-emerald-400" };

export default function FlowBuilderPage({
  apiPrefix = "/flow-builder",
  detailBasePath = "/flow-builder",
  accent = "indigo",
  title = "Visual Flow Builder",
  subtitle = "Drag and drop Data Cloud pipeline stages into the correct order to solve real business scenarios. Watch out — a few decoy nodes are mixed in to test whether you really understand the architecture.",
}) {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`${apiPrefix}/scenarios`).then((res) => setScenarios(res.data)).finally(() => setLoading(false));
  }, [apiPrefix]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="flow-builder-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="flow-builder-page">
      <div className="flex items-center gap-3 mb-2">
        <GitBranch className={`w-6 h-6 ${ACCENT_TEXT[accent]}`} />
        <span className={`text-xs font-bold uppercase tracking-[0.2em] ${ACCENT_TEXT[accent]}`}>Visual Practice Tool</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{title}</h1>
      <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-2xl">{subtitle}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {scenarios.map((s, i) => (
          <motion.div
            key={s.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }}
            className={`glass-panel rounded-2xl p-6 relative overflow-hidden bg-gradient-to-br ${DIFFICULTY_COLOR[s.difficulty] || ""}`}
            data-testid={`flow-builder-scenario-card-${s.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                <GitBranch className="w-5 h-5" />
              </div>
              {s.solved && (
                <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-400" data-testid={`flow-builder-solved-badge-${s.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Solved · {s.best_score}%
                </span>
              )}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider">{s.difficulty}</span>
            <h2 className="font-heading text-lg font-semibold text-white mt-1">{s.title}</h2>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed line-clamp-3">{s.description}</p>
            <div className="flex items-center justify-between mt-5">
              <span className="text-xs font-bold text-[#FBBF24] flex items-center gap-1"><Sparkles className="w-3 h-3" /> +{s.xp_reward} XP</span>
              <Link
                to={`${detailBasePath}/${s.id}`} data-testid={`flow-builder-enter-btn-${s.id}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 px-4 py-2 rounded-lg transition-colors"
              >
                {s.solved ? "Replay" : "Build It"} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
